import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import tinymce from 'tinymce';

import type { PopupListItem, Service } from 'webapp-types';
import {
	BillingFeature,
	PopupContextSwitchAction as ContextSwitchAction,
	ReportPopupShareTab,
} from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';

import onWindowEvents from 'webapp/modifiers/on-window-events';
import CommonCollapsiblePillOptionsComponent from 'webapp/components/common/collapsible-pill-options';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import NotificationsButtonComponent from 'webapp/components/notifications/button';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type Report from 'webapp/models-custom/report';

import type { PagelessTourSettings } from 'webapp/services/flow/tour/reports-pageless';
import type { SlidesTourSettings } from 'webapp/services/flow/tour/reports-slides';

interface Args {
	model: Report;
	changeViewMode: (isPageless: boolean) => void;
	closeEditor: () => void;
}

type Action =
	| 'toggleFavorite'
	| 'openTags'
	| 'openArchive'
	| 'duplicate'
	| 'deleteReport'
	| 'openShareSettings'
	| 'openGeneratePdf'
	| 'openAccountTemplateEditPopup'
	| 'openPublicTemplateEditPopup'
	| 'present'
	| 'verify';

type Option = PopupListItem<Action>;

export default class ReportsEditorHeaderComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/reminders') declare mmRemindersService: Service.ModelManager.Reminders;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('billing') declare billingService: Service.Billing;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('flow/tour/reports-pageless')
	declare pagelessTourService: Service.Flow.Tour.ReportsPageless;
	@service('flow/tour/reports-slides')
	declare slidesTourService: Service.Flow.Tour.ReportsSlides;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;

	pagelessTourSettings: PagelessTourSettings[] = [
		{
			id: TourId.REPORTS__PAGELESS_SHARE,
			checkStartConditions: this.checkPagelessShareTourStartConditions.bind(this),
		},
		{
			id: TourId.REPORTS__PAGELESS_VIEW_SWITCH,
			checkStartConditions: this.checkPagelessViewSwitchTourStartConditions.bind(this),
		},
	];

	slidesTourSetting: SlidesTourSettings = {
		id: TourId.REPORTS__PRESENT,
		checkStartConditions: () => true,
	};

	@tracked showPresentAsButton = true;

	trackingOrigin = 'editor';

	trackingObj = {
		event: TrackingEvent.REPORT_EDITED,
		data: { origin: this.trackingOrigin },
	};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// setup tour bindings
		schedule('afterRender', () => {
			this.setupTourBindings();
		});

		this.checkToShowPresentAsButton();
	}

	setupTourBindings() {
		this.pagelessTourSettings.forEach((tourSetting) => {
			this.pagelessTourService.bindTourCheck(tourSetting.id, this, () => {
				if (tourSetting.checkStartConditions()) {
					this.pagelessTourService.startTour(tourSetting.id);
				}
			});
		});

		this.slidesTourService.bindTourCheck(this.slidesTourSetting.id, this, () => {
			if (this.slidesTourSetting.checkStartConditions()) {
				this.slidesTourService.startTour(this.slidesTourSetting.id);
			}
		});
	}

	private checkPagelessShareTourStartConditions(): boolean {
		return this.isPageless && this.isSavedReport;
	}

	private checkPagelessViewSwitchTourStartConditions(): boolean {
		return this.isSavedReport;
	}

	get isPageless(): boolean {
		return this.args.model.isPageless;
	}

	get options(): Option[] {
		const model = this.args.model,
			options: Option[] = [];

		if (!this.showPresentAsButton) {
			options.push({
				icon: 'Play',
				text: 'Present',
				action: 'present',
				class: 'present', // for tour
			});
		}

		options.push({
			icon: 'Download',
			text: 'Download PDF',
			action: 'openGeneratePdf',
			class: 'download', // for tour
		});

		options.push({
			icon: 'Share',
			text: 'Share',
			action: 'openShareSettings',
			class: 'share-link', // for tour
		});

		options.push({
			icon: 'Tag',
			text: 'Tags',
			action: 'openTags',
			class: 'tags',
		});

		options.push({
			icon: 'Folder',
			text: 'PDF archive',
			action: 'openArchive',
		});

		options.push({
			class: model.favorite ? 'accent' : '',
			icon: model.favorite ? 'StarFill' : 'Star',
			text: model.favorite ? 'Unfavorite' : 'Favorite',
			action: 'toggleFavorite',
		});

		if (!model.isTemplate) {
			options.push({
				icon: 'Copy',
				text: 'Duplicate',
				action: 'duplicate',
			});

			options.push({
				icon: 'FolderUpload',
				text: 'Save as template',
				action: 'openAccountTemplateEditPopup',
				extra: this.billingService.accountTemplatesEnabled
					? undefined
					: {
							type: 'featureLimitation',
						},
			});
		}

		if (this.accountService.isPublictemplatesSpace) {
			options.push({
				icon: 'FolderUpload',
				text: `${model.isTemplate ? 'Edit' : 'Save as'} public template`,
				action: 'openPublicTemplateEditPopup',
			});
		}

		if (!model.isTemplate) {
			options.push({
				icon: 'Verified',
				text: model.isVerified ? 'Unverify' : 'Verify',
				action: 'verify',
				isDisabled: !model.verificationInfo,
			});
		}

		options.push({
			class: 'delete',
			text: 'Delete',
			action: 'deleteReport',
			icon: 'Trash',
		});

		return options;
	}

	get isSavedReport(): boolean {
		return !this.args.model.isNew;
	}

	get viewOptions(): { value: boolean; label: string; icon: IconName; isSelected: boolean }[] {
		return [
			{
				value: true,
				label: 'Page',
				icon: 'Pageless',
				isSelected: this.isPageless,
			},
			{
				value: false,
				label: 'Slides',
				icon: 'Slides',
				isSelected: !this.isPageless,
			},
		];
	}

	@action
	doAction(actionName?: string) {
		this[actionName as Action]();
	}

	openShareSettings(): void {
		this.mmReportsService.openSharePopup(this.args.model, 'editor', ReportPopupShareTab.LINK);

		this.pagelessTourService.checkTour();
	}

	openGeneratePdf(): void {
		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}

		const model = this.args.model;
		this.mmReportsService.openSharePopup(model, 'editor', ReportPopupShareTab.DOWNLOAD);
	}

	openAccountTemplateEditPopup(): void {
		this.mmReportsService.openAccountTemplateEditPopup(this.args.model, 'editor');
	}

	openPublicTemplateEditPopup(): void {
		this.mmReportsService.openPublicTemplateEditPopup(this.args.model);
	}

	toggleFavorite(): void {
		this.mmReportsService.toggleReportFavorite(this.args.model);
	}

	openTags(): void {
		const head = document.getElementById('report-editor_head');

		if (!head) {
			return;
		}

		const attachTo =
			head.querySelector<HTMLElement>('.tags') ||
			head.querySelector<HTMLElement>('.pill-option-more');
		if (attachTo) {
			this.mmTagsService.openTagsContext(attachTo, {
				model: this.args.model,
			});
		}
	}

	openArchive(): void {
		this.navigationService.transitionToRoute('reports.archive');
	}

	duplicate(): void {
		const duplicatePromise = this.mmReportsService.duplicateReport(this.args.model);

		if (!duplicatePromise) {
			return;
		}

		void duplicatePromise
			.then((newReport) => {
				this.analyticsService.event(TrackingEvent.REPORT_CREATED, { origin: 'editor' });
				this.navigationService.createContextSwitchModal().open({
					actionType: ContextSwitchAction.DUPLICATE,
					sourceItem: this.args.model,
					targetItem: newReport,
					onSwitch: () => {
						this.navigationService.transitionToRoute(
							'reports.editor.report',
							newReport.model,
						);
					},
				});
			})
			.catch();
	}

	@action
	present() {
		this.mmReportsService.startPresent(this.args.model, 'editor');
	}

	@action
	verify(): void {
		void this.mmReportsService.toggleVerification(this.args.model);
	}

	deleteReport(): void {
		const report = this.args.model;
		schedule('afterRender', () => {
			void this.mmReportsService.deleteReport(report).then(() => {
				this.close(true);
			});
		});
	}

	@action
	close(skipDelete = false) {
		const report = this.args.model;
		const isTemplate = report.isTemplate;
		const { closeEditor } = this.args;

		if (!isTemplate && this.isSavedReport && report.slideCount === 0 && !skipDelete) {
			this.mmReportsService
				.deleteReport(report)
				.catch(() => {
					// skip
				})
				.finally(() => {
					closeEditor();
				});
		} else {
			closeEditor();
		}
	}

	@action
	changeViewMode(isPageless: boolean): void {
		const report = this.args.model;

		this.analyticsService.event(TrackingEvent.SWITCH_REPORT_FORMAT, {
			origin: this.trackingOrigin,
			type: isPageless ? 'pageless' : 'slides',
		});

		// close all focused editors to prevent flickering
		tinymce.editors.forEach((editor) => {
			editor.ui.hide();
		});
		// if isNewlyCreated is true, editor always gets focused
		report.fragments.forEach((fragment) => {
			fragment.directPropUpdate({
				isNewlyCreated: false,
			});
		});

		this.popupService.closeOnlyContextPopups();
		this.args.changeViewMode(isPageless);
	}

	@action
	checkToShowPresentAsButton() {
		this.showPresentAsButton = window.innerWidth > 1064; // from @right_actions_breakpoints
	}

	override willDestroy() {
		super.willDestroy();

		this.pagelessTourSettings.forEach((tourSetting) => {
			this.pagelessTourService.unbindTourCheck(tourSetting.id, this);
		});

		this.slidesTourService.unbindTourCheck(this.slidesTourSetting.id, this);
	}
	<template>
		<div
			id="report-editor_head"
			{{onWindowEvents this.checkToShowPresentAsButton "resize" 200}}
		>

			<div class="left">
				<EditableModelPropertyComponent
					@model={{@model}}
					@canEdit={{this.isSavedReport}}
					@editableProperty="title"
					@trackingObj={{this.trackingObj}}
				/>
				<VerificationBadgeComponent
					@verification={{@model.verificationInfo}}
					@entityLabel="report"
					@size="md"
				/>
			</div>

			<div class="view-switcher-wrapper">
				<div class="view-switcher">
					{{#each this.viewOptions as |option|}}
						<a
							role="button"
							class="button switcher-btn
								{{if option.isSelected 'theme-blue' 'theme-grey-outlined'}}"
							{{on "click" (fn this.changeViewMode option.value)}}
						>
							<CommonUiIconComponent @name={{option.icon}} @size="md" />
							<span class="switcher-label">{{option.label}}</span>
						</a>
					{{/each}}
				</div>
			</div>

			<div class="right">

				{{#if this.isSavedReport}}
					<div class="dbx-ui-actions">

						<CommonCollapsiblePillOptionsComponent
							@itemsList={{this.options}}
							@doAction={{this.doAction}}
							@maxVisibleItems={{2}}
						/>

						{{#unless @model.isTemplate}}
							<NotificationsButtonComponent @model={{@model}} @origin="edit" />

							{{#if this.showPresentAsButton}}
								<a class="button present" {{on "click" this.present}}>
									<CommonUiIconComponent @name="PlayFill" @size="md" />
									Present
								</a>
							{{/if}}
						{{/unless}}

					</div>
				{{/if}}

				<a role="button" class="close" {{on "click" (fn this.close false)}}>
					<CommonUiIconComponent @name="Close" @size="lg" />
				</a>

			</div>
		</div>
	</template>
}
